import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight, Loader2, MessageCircle, Sparkles, X } from "lucide-react";
import { API } from "../lib/api";
import { LogoMark } from "./Logo";
import { useLang } from "../i18n";

const getSessionId = () => {
  let sid = localStorage.getItem("cabnow_chat_sid");
  if (!sid) {
    sid = crypto.randomUUID();
    localStorage.setItem("cabnow_chat_sid", sid);
  }
  return sid;
};

export const ChatWidget = () => {
  const { pathname } = useLocation();
  const { t, ta } = useLang();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const scrollRef = useRef(null);

  const suggestions = ta("chat.suggestions");

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, open]);

  if (pathname.startsWith("/admin")) return null;

  const send = async (text) => {
    const message = (text ?? input).trim();
    if (!message || streaming) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: message }, { role: "assistant", content: "" }]);
    setStreaming(true);
    try {
      const res = await fetch(`${API}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: getSessionId(), message }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "error");
      }
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const frames = buf.split("\n\n");
        buf = frames.pop();
        for (const frame of frames) {
          const line = frame.trim();
          if (!line.startsWith("data:")) continue;
          const payload = line.slice(5).trim();
          if (payload === "[DONE]") continue;
          try {
            const parsed = JSON.parse(payload);
            if (parsed.delta) {
              setMessages((m) => {
                const copy = [...m];
                copy[copy.length - 1] = { role: "assistant", content: copy[copy.length - 1].content + parsed.delta };
                return copy;
              });
            }
            if (parsed.error) throw new Error(parsed.error);
          } catch (e) {
            if (e instanceof SyntaxError) continue;
            throw e;
          }
        }
      }
    } catch {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", content: t("chat.error") };
        return copy;
      });
    } finally {
      setStreaming(false);
    }
  };

  return (
    <>
      <motion.button
        data-testid="chat-open-button"
        onClick={() => setOpen(!open)}
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ delay: 1.2, type: "spring", stiffness: 260, damping: 18 }}
        className="fixed bottom-6 right-6 z-50 flex h-14 w-14 items-center justify-center rounded-full bg-navy text-white shadow-2xl shadow-navy/30 transition-[background-color,transform] hover:scale-105 hover:bg-azure"
        aria-label={t("chat.aria_open")}
      >
        {open ? <X size={22} /> : <MessageCircle size={22} />}
      </motion.button>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="chat-panel"
            initial={{ opacity: 0, y: 24, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.96 }}
            transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
            className="fixed bottom-24 right-4 z-50 flex h-[32rem] w-[calc(100vw-2rem)] max-w-sm flex-col overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-2xl sm:right-6"
          >
            <div className="flex items-center gap-3 bg-navy px-5 py-4">
              <LogoMark size={32} />
              <div>
                <p className="font-serif text-lg font-semibold leading-tight text-white">Conciergerie CabNow</p>
                <p className="flex items-center gap-1.5 text-xs text-white/60">
                  <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" /> {t("chat.status")}
                </p>
              </div>
            </div>

            <div ref={scrollRef} className="flex-1 space-y-4 overflow-y-auto bg-sand px-4 py-5" data-testid="chat-messages">
              {messages.length === 0 && (
                <div>
                  <p className="rounded-2xl rounded-tl-md bg-white px-4 py-3 text-sm leading-relaxed text-navy/80 shadow-sm">
                    {t("chat.greeting")}
                  </p>
                  <div className="mt-4 space-y-2">
                    {suggestions.map((s, i) => (
                      <button
                        key={s}
                        data-testid={`chat-suggestion-${i}`}
                        onClick={() => send(s)}
                        className="block w-full rounded-full border border-azure/25 bg-white px-4 py-2.5 text-left text-xs font-semibold text-azure transition-colors hover:bg-azure hover:text-white"
                      >
                        {s}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <p
                    data-testid={`chat-message-${i}`}
                    dir="auto"
                    className={`max-w-[85%] whitespace-pre-wrap rounded-2xl px-4 py-3 text-sm leading-relaxed shadow-sm ${
                      m.role === "user" ? "rounded-br-md bg-azure text-white" : "rounded-tl-md bg-white text-navy/80"
                    }`}
                  >
                    {m.content}
                    {m.role === "assistant" && streaming && i === messages.length - 1 && !m.content && (
                      <Loader2 size={15} className="animate-spin text-azure" />
                    )}
                  </p>
                </div>
              ))}
            </div>

            <div className="border-t border-slate-100 bg-white p-3">
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  send();
                }}
                className="flex items-center gap-2"
              >
                <input
                  data-testid="chat-input"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  placeholder={t("chat.placeholder")}
                  maxLength={1000}
                  className="flex-1 rounded-full border border-slate-200 bg-sand px-4 py-2.5 text-sm text-navy outline-none transition-colors focus:border-azure"
                />
                <button
                  type="submit"
                  data-testid="chat-send-button"
                  disabled={streaming || !input.trim()}
                  className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-azure text-white transition-colors hover:bg-azure-hover disabled:opacity-40"
                  aria-label="Envoyer"
                >
                  <ArrowRight size={17} />
                </button>
              </form>
              <p className="mt-2 flex items-center justify-center gap-1 text-[10px] text-navy/40">
                <Sparkles size={10} className="text-gold" /> {t("chat.disclaimer")}
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
